// frontend/src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from "react";
import API, {
  login as loginAPI,
  register as registerAPI,
  getMe,
  refreshToken as refreshTokenAPI,
  logout as logoutAPI,
} from "../api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isSuperAdmin = () => user?.role === "super_admin";
  const isAdmin = () => user?.role === "admin" || user?.role === "super_admin";
  const isTeamMember = () => user?.isTeamMember === true;
  const isStaff = () => isAdmin() || isTeamMember();
  const isFarmer = () => user?.userType === "farmer" || user?.role === "farmer";
  const isCustomer = () =>
    user?.userType === "customer" || user?.role === "customer";
  const isSupplier = () =>
    user?.userType === "supplier" || user?.role === "supplier";

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          try {
            const { data } = await getMe();
            setUser({ ...parsed, ...data });
          } catch (error) {
            if (error.response?.status === 401) {
              try {
                const refreshResult = await refreshTokenAPI();
                if (refreshResult.data.token) {
                  const { data } = await getMe();
                  setUser({
                    ...parsed,
                    ...data,
                    token: refreshResult.data.token,
                  });
                  localStorage.setItem(
                    "user",
                    JSON.stringify({
                      ...parsed,
                      ...data,
                      token: refreshResult.data.token,
                    }),
                  );
                }
              } catch (refreshError) {
                localStorage.removeItem("user");
                setUser(null);
              }
            } else {
              throw error;
            }
          }
        } catch (error) {
          localStorage.removeItem("user");
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await loginAPI(email, password);
      const userData = response.data;

      if (userData && userData.token) {
        const safeUserData = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          role: userData.role || "customer",
          userType: userData.userType || "customer",
          phone: userData.phone || "",
          farmDetails: userData.farmDetails,
          bulkPurchaseEnabled: userData.bulkPurchaseEnabled,
          addresses: userData.addresses || [],
          isActive: userData.isActive,
          isVerified: userData.isVerified,
          supplierId: userData.supplierId || null,
          isTeamMember: userData.isTeamMember || false,
          teamRole: userData.teamRole || "",
          bio: userData.bio || "",
          avatar: userData.avatar || "",
          token: userData.token,
          refreshToken: userData.refreshToken,
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));
        setUser(safeUserData);
        return safeUserData;
      }
      throw new Error("Invalid response from server");
    } catch (error) {
      console.error("Login error:", error.response?.data || error.message);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await registerAPI(userData);
      const safeUserData = {
        _id: data._id,
        name: data.name,
        email: data.email,
        phone: data.phone || "",
        userType: data.userType || "customer",
        role: data.role || "customer",
        farmDetails: data.farmDetails,
        bulkPurchaseEnabled: data.bulkPurchaseEnabled,
        addresses: data.addresses || [],
        isActive: data.isActive !== undefined ? data.isActive : true,
        isVerified: data.isVerified !== undefined ? data.isVerified : false,
        supplierId: data.supplierId || null,
        isTeamMember: data.isTeamMember || false,
        teamRole: data.teamRole || "",
        bio: data.bio || "",
        token: data.token,
        refreshToken: data.refreshToken,
      };
      localStorage.setItem("user", JSON.stringify(safeUserData));
      setUser(safeUserData);
      return safeUserData;
    } catch (error) {
      console.error("Register error:", error.response?.data || error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutAPI();
    } catch (error) {
      console.error("Logout API error:", error);
    }
    localStorage.removeItem("user");
    localStorage.removeItem("cart");
    setUser(null);
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const { data } = await getMe();
      const updatedUser = { ...user, ...data };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        loading,
        isRefreshing,
        isSuperAdmin,
        isAdmin,
        isTeamMember,
        isStaff,
        isFarmer,
        isCustomer,
        isSupplier,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
